// Shared layout for auth pages (login, register, verify, password reset).
// Centered card over the hero gradient background, with the AeroChat wordmark
// above. Forms render as children.

import React from 'react';
import { Box, Container, Paper, Typography, Link } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Brand from './Brand';
import LanguageSwitcher from './LanguageSwitcher';
import { colors, shadows } from '../theme';
import { isRaphaDomain } from '../services/subdomain';

interface AuthLayoutProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  footerLeft?: React.ReactNode;
  footerRight?: React.ReactNode;
}

export default function AuthLayout({
  title,
  subtitle,
  children,
  footerLeft,
  footerRight,
}: AuthLayoutProps) {
  const { t } = useTranslation('common');
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 6,
        px: 2,
        backgroundImage: colors.gradHero,
      }}
    >
      <Container
        maxWidth="sm"
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
      >
        {isRaphaDomain() && (
          <Box sx={{ position: 'absolute', top: 24, right: 24 }}>
            <LanguageSwitcher />
          </Box>
        )}
        <Box sx={{ mb: 5 }}>
          <Brand size="lg" href="/" subtitle={t('brand.subtitle')} />
        </Box>

        <Paper
          sx={{
            width: '100%',
            maxWidth: 440,
            p: { xs: 3, sm: 5 },
            backgroundColor: 'rgba(18, 24, 38, 0.8)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            boxShadow: shadows.xl,
            border: `1px solid ${colors.border}`,
          }}
        >
          <Typography
            variant="h4"
            sx={{ mb: subtitle ? 1 : 4, fontWeight: 700 }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              {subtitle}
            </Typography>
          )}

          {children}
        </Paper>

        {(footerLeft || footerRight) && (
          <Box
            sx={{
              width: '100%',
              maxWidth: 440,
              mt: 3,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '0.875rem',
            }}
          >
            <Box>{footerLeft}</Box>
            <Box>{footerRight}</Box>
          </Box>
        )}

        <Box
          sx={{
            mt: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <Typography variant="caption" sx={{ color: colors.textFaint }}>
            {t('footer.copyright', { year: new Date().getFullYear() })}
          </Typography>
          <Typography
            component="a"
            href="/privacy"
            variant="caption"
            sx={{ color: colors.textMuted, textDecoration: 'none' }}
          >
            {t('footer.privacy')}
          </Typography>
          <Typography
            component="a"
            href="/terms"
            variant="caption"
            sx={{ color: colors.textMuted, textDecoration: 'none' }}
          >
            {t('footer.terms')}
          </Typography>
          <Typography
            component="a"
            href="/liability"
            variant="caption"
            sx={{ color: colors.textMuted, textDecoration: 'none' }}
          >
            {t('footer.liability')}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

export function AuthLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link href={href} sx={{ color: colors.brand, fontWeight: 500 }}>
      {children}
    </Link>
  );
}
